import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { divergenceReportsQueryOptions } from "@/server/divergence";
import { DashboardLayout } from "@/components/dashboard-layout";
import { DivergenceReportsTable } from "./-components/divergences-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { getDivergenceSearchMaxDaysOptions } from "@/server/settings";
import { getDefaultDivergenceDateRange } from "./-utils/dates";
import { checkIfUserMayRead, ContextUser, getNextRedirectRoute } from "@/lib/utils";
import { DivergenceReportDetails } from "./-components/divergence-details";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";

export type DivergenceReport = {
  id: number;
  username: string;
  userAgent: string;
  service: string;
  callId: string;
  caller: string;
  created: string;
  reason: string;
  message: string;
};

export type DivergenceSearch = {
  divergenceId?: number;
  search?: string;
  after?: string;
  before?: string;
  page?: number;
  pageSize?: number;
};

type DivergenceSearchUpdate = Partial<DivergenceSearch> & { replace?: boolean };

export const Route = createFileRoute("/_dashboard/divergences")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): DivergenceSearch => {
    return {
      divergenceId: (search.divergenceId as number) || undefined,
      search: (search.search as string) || undefined,
      after: (search.after as string) || undefined,
      before: (search.before as string) || undefined,
      page: search.page ? Number(search.page) : undefined,
      pageSize: search.pageSize ? Number(search.pageSize) : undefined,
    };
  },
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "DIVERGENCE");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
  },
  loader: () => {
    return {
      crumb: "Divergences",
    };
  },
});

function RouteComponent() {
  return (
    <DashboardLayout submenu={null}>
      <DivergencesPageContent />
    </DashboardLayout>
  );
}

function DivergencesPageContent() {
  const { divergenceId, search, after: from, before: to, page, pageSize } = Route.useSearch();
  const navigate = Route.useNavigate();
  const openRightSidebar = useRightSidebarOpen();
  const closeRightSidebar = useRightSidebarClose();

  const maxDateRangeQuery = useQuery(getDivergenceSearchMaxDaysOptions);
  const maxDateRange = Number(maxDateRangeQuery.data?.data.systemValue) || undefined;

  const defaultRange = useMemo(() => {
    if (!maxDateRange) {
      return { after: null, before: null };
    }
    return getDefaultDivergenceDateRange(maxDateRange);
  }, [maxDateRange]);

  const hasAppliedInitialDefaults = useRef(false);

  const [after, setAfter] = useState(from ?? "");
  const [before, setBefore] = useState(to ?? "");

  const syncSearch = useCallback(
    (updates: DivergenceSearchUpdate) => {
      const nextAfter = updates.after ?? after ?? "";
      const nextBefore = updates.before ?? before ?? "";
      if (!nextAfter.length || !nextBefore.length) return;

      const next = {
        divergenceId: "divergenceId" in updates ? updates.divergenceId : divergenceId,
        search: "search" in updates ? updates.search : search,
        after: nextAfter,
        before: nextBefore,
        page: "page" in updates ? updates.page : page,
        pageSize: "pageSize" in updates ? updates.pageSize : pageSize,
      };

      if (
        from === next.after &&
        to === next.before &&
        search === next.search &&
        divergenceId === next.divergenceId &&
        page === next.page &&
        pageSize === next.pageSize
      ) {
        return;
      }

      navigate({
        replace: updates.replace ?? false,
        search: {
          divergenceId: next.divergenceId || undefined,
          search: next.search || undefined,
          after: next.after,
          before: next.before,
          page: next.page || undefined,
          pageSize: next.pageSize || undefined,
        },
      });
    },
    [after, before, from, to, divergenceId, search, page, pageSize, navigate]
  );

  const divergenceQueryOptions = useMemo(
    () =>
      divergenceReportsQueryOptions({
        after: after || undefined,
        before: before || undefined,
      }),
    [after, before]
  );
  const divergenceQuery = useQuery(divergenceQueryOptions);
  const divergenceReports: DivergenceReport[] = useMemo(
    () =>
      (divergenceQuery.data || [])
        .filter((item) => item.id != null && item.id >= 0)
        .map((item) => ({
          id: item.id!,
          username: item.username ?? "",
          userAgent: item.userAgent ?? "",
          service: item.service ?? "",
          callId: item.callId ?? "",
          caller: item.caller ?? "",
          created: item.created ? new Date(item.created * 1000).toISOString() : "",
          reason: item.reason ?? "",
          message: item.truncatedMessage ?? "",
        })),
    [divergenceQuery.data]
  );

  useEffect(() => {
    if (!divergenceId) {
      closeRightSidebar("divergenceId");
      return;
    }
    if (divergenceQuery.isLoading) return;

    let componentToRender: React.ReactNode | null = null;

    const report = divergenceReports.find((r) => r.id === divergenceId);
    if (report) {
      componentToRender = <DivergenceReportDetails id={report.id} />;
    }

    if (!componentToRender) {
      closeRightSidebar("divergenceId");
      return;
    }

    openRightSidebar({
      component: componentToRender,
      paramKeys: "divergenceId",
    });
  }, [divergenceId, divergenceReports, divergenceQuery.isLoading, openRightSidebar, closeRightSidebar]);

  useEffect(() => {
    if (divergenceQuery.isError) {
      const message =
        divergenceQuery.error instanceof Error
          ? divergenceQuery.error.message
          : "Failed to load divergence reports. Please try again later.";
      toast.error(message);
    }
  }, [divergenceQuery.isError, divergenceQuery.error]);

  useEffect(() => {
    setAfter(from ?? "");
  }, [from]);

  useEffect(() => {
    setBefore(to ?? "");
  }, [to]);

  useEffect(() => {
    if (!defaultRange.after || !defaultRange.before) return;
    if (from || to) {
      hasAppliedInitialDefaults.current = true;
      return;
    }
    if (hasAppliedInitialDefaults.current) return;

    setAfter(defaultRange.after);
    setBefore(defaultRange.before);
    syncSearch({ after: defaultRange.after, before: defaultRange.before, replace: true });
    hasAppliedInitialDefaults.current = true;
  }, [from, to, defaultRange.after, defaultRange.before, syncSearch]);

  const resetFilters = useCallback(() => {
    if (!defaultRange.after || !defaultRange.before) return;

    setAfter(defaultRange.after);
    setBefore(defaultRange.before);
    syncSearch({
      after: defaultRange.after,
      before: defaultRange.before,
      search: undefined,
      page: undefined,
      pageSize: undefined,
      replace: true,
    });
  }, [defaultRange.after, defaultRange.before, syncSearch]);

  const clearDateRange = useCallback(() => {
    setAfter("");
    setBefore("");
    navigate({
      replace: true,
      search: {
        divergenceId: divergenceId || undefined,
        search: search || undefined,
        page: page || undefined,
        pageSize: pageSize || undefined,
      },
    });
  }, [divergenceId, search, page, pageSize, navigate]);

  return (
    <DivergenceReportsTable
      divergenceReports={divergenceReports}
      isLoading={divergenceQuery.isLoading}
      defaultDivergenceId={divergenceId}
      defaultSearch={search}
      before={before}
      after={after}
      setAfter={setAfter}
      setBefore={setBefore}
      defaultPage={page}
      defaultPageSize={pageSize}
      maxDateRange={maxDateRange ?? 30}
      syncSearch={syncSearch}
      resetFilters={resetFilters}
      clearDateRange={clearDateRange}
      initialAfter={defaultRange.after ?? undefined}
      initialBefore={defaultRange.before ?? undefined}
    />
  );
}

export default RouteComponent;
